import { isWarnable, LabelContentItem, LabelDialogParts, setOrDeleteLabel, } from '@tableau/catalog-ui-temp';
import { restApiService } from '@tableau/vizportalclient-common-components';
import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { getItemsWithCorrectLuids } from '~src/Collections/sagas/CollectionSagas';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { delayPromise } from '~src/commonInfra/utils/Delay';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { fetchContent, reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { SetDataQualityAction } from '../../actions/labels/SetDataQualityAction';
import { defaultInitialState, DialogBodyProps, DialogState, getActionDialogOptions } from '../../components/labels/LabelDialog';


export function* handleSetDataQuality(action: SetDataQualityAction) {
  try {
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, [action.item]);

    const first = mutatableContentCopy[0] ?? undefined;
    const item: IContentInfo = yield fetchContent(first);

    yield call(openDataQualityDialog, { type: action.type, item });

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }

  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openDataQualityDialog(action: SetDataQualityAction): PromiseLike<unknown> {
  /*
   ToDo: Story 1424438
   Change let into a const and remove the if block
   */
  const itemWithCorrectLuid = getItemsWithCorrectLuids([action.item])[0];
  let { luid: contentId, name: contentName, contentType } = itemWithCorrectLuid;
  if (!isWarnable(contentType)) {
    throw new Error(`${contentType} is not warnable`);
  }

  const contentItem: LabelContentItem = { contentId, contentName, contentType };

  return openDataQualityDialogCatalog({ contentItem, action });
}

export function openDataQualityDialogCatalog({ contentItem, action, onSubmit }: {
  readonly contentItem: LabelContentItem;
  readonly action?: SetDataQualityAction;
  readonly onSubmit?: () => void;
}): Promise<unknown> {

  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  const {makeJsonRestApiRequest} = restApiService({siteLuid});
  const doAction: (formData: DialogState) => Promise<unknown> = ({ luid, isActive, isSevere, message, labelValue }) => {
    return setOrDeleteLabel({
      luid: luid,
      makeJsonRestApiRequest,
      category: 'warning',
      contentItems: [contentItem],
      labelContent: {
        active: isActive,
        elevated: isSevere,
        message,
        value: labelValue
      }
    }).then(async (_: unknown) => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.labelUpdateSuccess({
        LABEL_CATEGORY: Messages.labelCategoryWarning(),
        CONTENT_TYPE: LabelDialogParts.getLabelForContentType(contentItem.contentType),
        CONTENT_NAME: contentItem.contentName, }),
      });
      if (contentItem.contentType === 'column') {
        /**
         * Columns are a special case and should wait an additional 3 seconds for a total of 5 which is the current p95 of graph ingestion time
         * Until we can make architectural improvements to the Labels UI to optimistically update our state or the graph's p95 improves
         */
        await delayPromise(3000);
      }
      if (onSubmit) {
        ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
        onSubmit();
      }
    }).catch((_: unknown) => ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.labelUpdateError({
        LABEL_CATEGORY: Messages.labelCategoryWarning(),
        CONTENT_TYPE: LabelDialogParts.getLabelForContentType(contentItem.contentType),
        CONTENT_NAME: contentItem.contentName
      }),
    }));
  };

  const initialBodyProps: DialogBodyProps = { contentItem };
  const modalInstance = openReactActionDialog(getActionDialogOptions({
    initialBodyProps,
    initialFormData: defaultInitialState('warning'),
    doAction,
    dialogType: 'warning',
  }));

  return modalInstance.result;
}
